import { useLocation, useParams } from "react-router-dom";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import { canContribute } from "../hartUtils";
import { useModelDetails } from "../hartHooks";
import { Button, Box, Paper } from "@mui/material";
import SessionFormFields, { validateSession, sessionNullableFields } from "../components/SessionFormFields";
import AssociationsSection from "../components/associations";

// ========================================
// MODEL CONFIG
// ========================================
const MODEL_CONFIG = {
  apiEndpoint: '/hart/sessions',
  idParamName: 'sessionId',
  displayName: 'Session',
  displayField: 'session_id',
  FormComponent: SessionFormFields,
  dataKey: 'session',
  validateFn: validateSession,
  nullableFields: sessionNullableFields,
};
// ========================================

export const SessionDetailsPage = () => {
  const { projectId, [MODEL_CONFIG.idParamName]: idParam } = useParams();
  const location = useLocation();

  const {
    data,
    setData,
    editable,
    submitting,
    loading,
    error,
    validationErrors,
    handleSubmit,
    handleEditToggle,
    handleBack,
  } = useModelDetails({
    apiEndpoint: MODEL_CONFIG.apiEndpoint,
    idParam,
    initialData: location.state?.[MODEL_CONFIG.dataKey],
    validateFn: MODEL_CONFIG.validateFn,
    nullableFields: MODEL_CONFIG.nullableFields,
  });

  const canEditRole = canContribute(projectId);
  const { FormComponent, displayName, displayField } = MODEL_CONFIG;

  if (loading) return <p>Loading {displayName} details...</p>;
  if (error) return <p>Error loading {displayName}: {error}</p>;

  return (
    <>
      <PageHeader
        title={`${displayName}: ${displayField ? (data[displayField] || idParam) : idParam}`}
        action={
          canEditRole && (
            <Button
              variant="contained"
              color={editable ? "secondary" : "primary"}
              aria-label={`Edit ${displayName}`}
              onClick={handleEditToggle}
              sx={{ '&:hover': { backgroundColor: 'secondary.main' } }}
            >
              {editable ? "Cancel Edit" : `Edit ${displayName}`}
            </Button>
          )
        }
      />
      <Paper sx={{ p: 3, mb: 3, mt: 5 }}>
        <FormComponent data={data} editable={editable} onChange={setData} errors={validationErrors} displayName={displayName} projectId={projectId} />
      </Paper>

      {/* Associations Section */}
      {!loading && data && data.session_id && (
        <AssociationsSection
          parentId={String(data.session_id)}
          parentData={data}
          parentType="session"
          associations={['vendors', 'host_recruiter', 'facilities']}
          editable={editable}
          editableAssociationCallbacks={{
            vendors: (vendors) => setData(prev => ({
              ...prev,
              vendor: vendors[0] || null, // Single vendor relationship
              vendor_id: vendors[0]?.vendor_id || null,
            })),
            host_recruiter: (hostRecruiters) => setData(prev => ({
              ...prev,
              hosts: hostRecruiters.filter(hr => hr.type === 'Host'),
              recruiters: hostRecruiters.filter(hr => hr.type === 'Recruiter'),
              // Send combined array with vendor_id, type, notes for backend
              host_recruiter: hostRecruiters.map(hr => ({
                vendor_id: hr.vendor_id,
                type: hr.type,
                notes: hr.notes || '',
              })),
            })),
            facilities: (facilities) => setData(prev => ({
              ...prev,
              facility: facilities[0] || null, // Single facility relationship
              facility_id: facilities[0]?.facility_id || null,
            })),
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {editable && canEditRole && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Submit"}
          </Button>
        )}
      </Box>
    </>
  );
};

const routeArgs = {
  element: <SessionDetailsPage />,
};

export default buildRoute(routeArgs);
